import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { useRouter } from 'next/router';
import { Alert } from '@mui/material';
import {
  addressesActions,
  asyncActions,
  transformAddress,
} from '@docavenue/core';
import { Address } from '@maiia/model/generated/model/api-patient/api-patient';
import Autocomplete from './Autocomplete';
import { hcdActions } from '../../../src/actions';
import { useTranslation } from '@/src/i18n';
import { DEFAULT_ADDRESSES } from '@/src/constants';

const AddressInput = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { t } = useTranslation();
  const selectedItemIndex = useSelector(state =>
    get(state, 'addresses.selectedItemIndex'),
  );
  const suggestion = useSelector(state => get(state, 'suggestions.item'));
  const [suggestionAddresses, setSuggestionAddresses] = useState<Address[]>(
    DEFAULT_ADDRESSES,
  );
  const [isError, setIsError] = React.useState(false);

  const submit = React.useCallback(
    address => {
      if (!suggestion || !address) {
        // eslint-disable-next-line no-console
        console.error('unknown address or suggestion', address, suggestion);
        return;
      }
      const {
        type,
        label,
        shortName: addressShortName,
        longitude,
        latitude,
        zipCode,
      } = address;

      const { shortName: specialityShortName } = suggestion;
      let query: any = {
        speciality: specialityShortName,
        locality: label,
      };

      if (['municipality', 'department', 'region'].includes(type)) {
        query = {
          ...query,
          locality: `${zipCode}-${addressShortName}`,
        };
      } else {
        query = {
          ...query,
          longitude,
          latitude,
        };
      }
      router.push({
        pathname: '/[speciality]/[locality]',
        query,
      });
    },
    [suggestion],
  );

  const onSearch = React.useCallback(async value => {
    if (!value) {
      setSuggestionAddresses(DEFAULT_ADDRESSES);
      return;
    }
    try {
      const data = await asyncActions(
        dispatch,
        addressesActions.search(
          value,
          {
            type: ['region+department', 'municipality', ''],
          },
          { scope: 'pat-public' },
        ),
      );
      setIsError(false);
      if (data.length) {
        const _addresses = data.map(transformAddress);
        setSuggestionAddresses(_addresses);
      } else setSuggestionAddresses(DEFAULT_ADDRESSES);
    } catch {
      setIsError(true);
    }
  }, []);

  const onChange = React.useCallback(
    item => {
      if (!item) return;
      dispatch(hcdActions.getActions().reset());
      dispatch(addressesActions.setItem(item));
      submit(item);
    },
    [submit],
  );

  // Redirect to search suggestion if it not have
  React.useEffect(() => {
    if (!suggestion) {
      router.push('/search/suggestions');
    }
  }, []);
  return (
    <div className="input-dropdown" data-cy="search-mobile-address">
      <Autocomplete
        showQuickAddress
        onSearch={onSearch}
        onChange={onChange}
        suggestions={suggestionAddresses}
        placeholder={t('placeholder-where')}
        selectedItemIndex={selectedItemIndex}
        initialInputValue=""
        iconComponent={null}
        allowClear
        useHighlight
        focusInput
        topElement={
          isError && (
            <Alert severity="error" icon={false}>
              {t('error:search_unavailable')}
            </Alert>
          )
        }
      />
    </div>
  );
};

export default AddressInput;
